import { ReactNode } from 'react';
import { Navigate } from '@tanstack/react-router';
import { ShieldX } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, type UserRole } from '@/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface OperatorGuardProps {
  children: ReactNode;
  fallback?: ReactNode | undefined;
  redirectTo?: string | undefined;
}

function hasOperatorPermission(roles: UserRole[]): boolean {
  return roles.some((role) => hasPermission(role, 'write'));
}

export function OperatorGuard({ children, fallback, redirectTo }: OperatorGuardProps) {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" label="Checking permissions..." />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    if (redirectTo) {
      return <Navigate to={redirectTo} />;
    }
    return <Navigate to="/" />;
  }

  const isOperator = hasOperatorPermission(user.roles);

  if (!isOperator) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <ShieldX className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You don&apos;t have permission to access this area. Operator privileges are required.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              If you believe this is an error, please contact your administrator.
            </p>
            <Button asChild>
              <a href="/">Return to Dashboard</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
